import { ActivityType } from '@app/components/imports/utils';
import { Icons } from '@shared/components/content/icons';
import { CheckboxMultipleOption } from '@shared/components/form/checkbox-multiple-form-field';

/**
 * The selectable activity types on the import activity-types step. "Other
 * goods" is a catch-all that can only be selected alongside Food or Packaging —
 * it is disabled until a primary type is chosen (see `GoodsImportTypePage`).
 *
 * The descriptions are report-type aware: the shared wizard serves both
 * purchased goods ("you have purchased" / "you buy") and sold goods ("you have
 * sold" / "you sell"), so the copy is parameterised by `isSales`.
 */
export const getActivityTypeOptions = (
  isSales: boolean
): (CheckboxMultipleOption & { value: ActivityType })[] => [
  {
    value: 'food',
    label: 'Food & drink',
    icon: <Icons.carrot className="size-5 shrink-0 text-teal-600" />,
    description: isSales
      ? 'The food and drink items that you sell. These items will be matched to a basic food or modelled with a recipe where appropriate'
      : 'The food and drink items that you purchase. These items will be matched to a basic food or modelled with a recipe where appropriate',
  },
  {
    value: 'packaging',
    label: 'Packaging',
    icon: <Icons.package className="size-5 shrink-0 text-teal-600" />,
    description: isSales
      ? 'Items you use to package, store, and serve the products you sell. These will be matched to standard packaging items.'
      : 'Items you purchase to package, store, and transport your products. These will be matched to standard packaging items.',
  },
  {
    value: 'other',
    label: 'Other goods',
    icon: <Icons.cookingPot className="size-5 shrink-0 text-teal-600" />,
    description:
      'Other non-food or packaging items included in your export, such as cleaning products or kitchenware. These items will not be assessed.',
  },
];

/** True when an activity type counts as a primary (non-catch-all) selection. */
export const isPrimaryActivityType = (type: ActivityType) =>
  type === 'food' || type === 'packaging';
